/**
 * The one-liner an operator runs on the Collector host, and the requirements
 * that decide whether it will work.
 *
 * A plain module rather than constants inside the wizard step, for the reason
 * `log-source-table-frame` exists: the step is `lazy()`, and the eager skeleton
 * has to render the same chrome — importing from the step would drag its chunk
 * into the eager bundle.
 */

/**
 * The enrollment token the real installer will carry.
 *
 * **Authored, and deliberately elided in the middle** — the design frame shows
 * it that way, and a complete-looking token here would be one somebody pastes
 * into a terminal and waits on. The real one is minted per Collector by an
 * endpoint that does not exist yet; when it does, it arrives as an argument to
 * {@link buildInstallCommand} and this constant is deleted.
 */
const ENROLLMENT_TOKEN = 'rat_5f3c…9a1b'

/** What the code block's header chip says, and the shell the command assumes. */
export const INSTALL_COMMAND_SHELL = 'bash'

/**
 * Stands in for the Collector's name until one is typed.
 *
 * The command stays on screen with a blank name rather than disappearing —
 * seeing what you are about to run is the whole point of the block — but it says
 * plainly which part is still missing, and the copy button is disabled so
 * nothing half-formed reaches a terminal.
 */
export const COLLECTOR_NAME_PLACEHOLDER = '<collector name>'

/**
 * The install one-liner, with the operator's Collector name interpolated.
 *
 * The name is what ties the host to the row that appears in AIR®, which is why
 * it is echoed back in the command rather than only living in the form field:
 * the operator is about to paste this into a terminal on a different machine,
 * and the string in their clipboard is the last chance to notice it says the
 * wrong site.
 *
 * The trailing comment is part of the command on purpose — it is what the
 * installer checks, and it puts the static-IP requirement in the terminal
 * scrollback where the failure will happen.
 */
export function buildInstallCommand(collectorName: string): string {
	const name = collectorName.trim() || COLLECTOR_NAME_PLACEHOLDER

	return [
		'curl -fsSL https://get.bitlyft.com/collector | sudo bash -s -- \\',
		`  --token ${ENROLLMENT_TOKEN}  --name "${name}"`,
		'',
		'# checks: connectivity · dependencies · static IP (DHCP blocks install)',
	].join('\n')
}

/**
 * What the host must be before the one-liner can succeed.
 *
 * `critical` is the one an operator learns about too late: DHCP does not fail
 * loudly at install time on every distro, and a Collector whose address moves
 * stops receiving syslog with no error anywhere — so it is the one phrase on the
 * line that carries colour, exactly as the Collector detail page does with the
 * same fact.
 */
export const COLLECTOR_HOST_REQUIREMENTS = [
	{ label: 'Linux VM (Debian / Ubuntu)', critical: false },
	{ label: 'Outbound HTTPS (443) only', critical: false },
	{ label: 'Static IP required — DHCP blocks install', critical: true },
] as const
